import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import {
  Base,
  BaseCorrugationSpan,
  BaseOrientation,
  BaseType,
  getBaseCorrugationSpan,
  getBaseOrientations,
  getBaseTypes,
  getDistanceOptions,
} from "../../../../../http/border/types/bases";
import { WaterproofKit } from "../../../../../http/border/types/waterproofKits";
import { useBasesApi } from "../../../../../http/border/useBases";
import { useWaterproofKitsApi } from "../../../../../http/border/useWaterproofKits";
import { AdminItem, SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import { itemLabel } from "../../../../../utils";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Numeric from "../../../../ui/Numeric";
import { CustomAsyncSelect, CustomSelect } from "../../../../ui/select/CustomSelect";
import Switch from "../../../../ui/Switch";

interface BaseFormBody {
  item: AdminItem | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  isVertical: boolean;
  distance: number;
  type: BaseType;
  isReclinableUpright: boolean;
  baseOrientation?: BaseOrientation;
  corrugationSpan?: BaseCorrugationSpan;
  waterproofKit?: WaterproofKit | null;
  width: number | null;
  height: number | null;
}

interface BaseFormProps {
  base?: Base;
  onClose: () => void;
  isDisabled?: boolean;
  isLoading?: boolean;
}

const BaseForm: FC<BaseFormProps> = ({ base, onClose, isDisabled, isLoading }: BaseFormProps) => {
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const [defaultItemOptions, setDefaultItemOptions] = useState<SearchAdminItem[]>([]);
  const [defaultWaterproofKitOptions, setDefaultWaterproofKitOptions] = useState<WaterproofKit[]>([]);

  const baseTypes = getBaseTypes(i18n.translation);
  const baseOrientations = getBaseOrientations(i18n.translation);
  const corrugationSpans = getBaseCorrugationSpan;
  const distanceOptions = getDistanceOptions;

  const basesApi = useBasesApi();
  const itemsApi = useItemsApi();
  const waterproofKitApi = useWaterproofKitsApi();

  const defaultValues = {
    order: base?.order ?? 50,
    visibility: base?.visibility ?? "hidden",
    distance: base?.distance,
    isVertical: base?.isVertical,
    isReclinableUpright: base?.isReclinableUpright,
    type: baseTypes.find((x) => x.value == base?.type),
    baseOrientation: baseOrientations.find((x) => x.value == base?.baseOrientation),
    corrugationSpan: corrugationSpans.find((x) => x.value == base?.corrugationSpan),
    waterproofKit: base?.waterproofKit,
    item: base?.item,
    width: base?.width,
    height: base?.height,
  };

  const onSubmit = () => async (formData: BaseFormBody) => {
    setIsSubmitting(true);

    if (base?.item.id) {
      basesApi
        .updateAdminBase(base.item.id, {
          order: formData.order!,
          visibility: formData.visibility,
          distance: formData.distance,
          isVertical: formData.isVertical,
          type: formData.type.value,
          isReclinableUpright: formData.isReclinableUpright,
          corrugationSpan: formData.corrugationSpan?.value,
          baseOrientation: formData.baseOrientation?.value,
          waterproofKitId: formData.waterproofKit?.item.id,
          width: formData.width!,
          height: formData.height!,
        })
        .then(() => onClose())
        .catch((err) => dispatch(showErrorNotification(i18n, err)))
        .finally(() => setIsSubmitting(false));
    } else {
      basesApi
        .insertAdminBase({
          itemId: formData.item?.id!,
          order: formData.order!,
          visibility: formData.visibility,
          distance: formData.distance,
          isVertical: formData.isVertical,
          type: formData.type.value,
          isReclinableUpright: formData.isReclinableUpright,
          corrugationSpan: formData.corrugationSpan?.value,
          baseOrientation: formData.baseOrientation?.value,
          waterproofKitId: formData.waterproofKit?.item.id,
          width: formData.width!,
          height: formData.height!,
        })
        .then(() => onClose())
        .catch((err) => dispatch(showErrorNotification(i18n, err)))
        .finally(() => setIsSubmitting(false));
    }
  };

  return (
    <Form<BaseFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isDisabled || isLoading || isSubmitting}
      secondaryButton={
        <Button buttonProps={{ onClick: () => onClose() }} type="secondary">
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={onSubmit}
    >
      {({ control, setValue, watch, reset }) => {
        const isVertical = !!watch("isVertical");
        const isReclinableUpright = !!watch("isReclinableUpright");

        const baseOrientation = watch("baseOrientation");

        useEffect(() => {
          reset(defaultValues);
        }, [base]);

        useEffect(() => {
          if (!baseOrientation) {
            setValue("corrugationSpan", undefined, { shouldDirty: true, shouldValidate: true });
          }
        }, [baseOrientation, setValue]);

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                      {({ labelId }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef(new AbortController());

                        const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                        // Fetch initial data on mount
                        useEffect(() => {
                          loadOptions(searchTerm.current).then(setDefaultItemOptions);
                        }, []);

                        return (
                          <CustomAsyncSelect
                            {...field}
                            inputId={labelId}
                            defaultOptions={defaultItemOptions}
                            isDisabled={isDisabled || isLoading || isSubmitting || !!base}
                            loadOptions={loadOptions}
                            isClearable
                            getOptionLabel={itemLabel}
                            getOptionValue={(x) => x.id}
                            isInvalid={fieldState.invalid}
                            onChange={(value) => setValue("item", value ?? null)}
                          />
                        );
                      }}
                    </FormField>
                  </>
                );
              }}
            />
            <Controller
              name="distance"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.distance} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <CustomSelect<(typeof distanceOptions)[number]>
                      name={field.name}
                      onBlur={field.onBlur}
                      inputId={labelId}
                      options={distanceOptions}
                      isDisabled={field.disabled || isDisabled || isLoading || isSubmitting}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(x) => x.label}
                      getOptionValue={(x) => String(x.value)}
                      isInvalid={isInvalid}
                      isClearable
                      value={distanceOptions.find((opt) => opt.value === field.value) ?? null}
                      onChange={(option) => setValue("distance", option?.value ?? 0)}
                    />
                  )}
                </FormField>
              )}
            />
            <FormField label={i18n.translation.common.isVertical} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={isVertical}
                  disabled={isDisabled || isLoading || isSubmitting}
                  onChange={(checked) => setValue("isVertical", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.isReclinableUpright} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={isReclinableUpright}
                  disabled={isDisabled || isLoading || isSubmitting}
                  onChange={(checked) => setValue("isReclinableUpright", checked)}
                />
              )}
            </FormField>
            <Controller
              name="width"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.width} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("width", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="height"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.height} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("height", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="type"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.type} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <CustomSelect
                      {...field}
                      inputId={labelId}
                      options={baseTypes}
                      isDisabled={isDisabled || isLoading || isSubmitting}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(x) => x.label}
                      getOptionValue={(x) => x.value}
                      isInvalid={isInvalid}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="baseOrientation"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.baseOrientation} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <CustomSelect
                      {...field}
                      inputId={labelId}
                      options={baseOrientations}
                      isDisabled={isDisabled || isLoading || isSubmitting}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(x) => x.label}
                      getOptionValue={(x) => x.value}
                      isInvalid={isInvalid}
                      isClearable
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="corrugationSpan"
              control={control}
              rules={{ required: !!baseOrientation }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.common.corrugationSpan}
                  error={fieldState.error}
                  isOptional={!baseOrientation}
                >
                  {({ labelId, isInvalid }) => (
                    <CustomSelect
                      {...field}
                      inputId={labelId}
                      options={corrugationSpans}
                      isDisabled={isDisabled || isLoading || isSubmitting || !baseOrientation}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(x) => x.label}
                      getOptionValue={(x) => String(x.value)}
                      isInvalid={isInvalid}
                      value={field.value ?? null}
                      onChange={(value) =>
                        setValue("corrugationSpan", value ?? undefined, { shouldDirty: true, shouldValidate: true })
                      }
                      isClearable
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="waterproofKit"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <FormField label={i18n.translation.waterproofKits.singular} error={fieldState.error} isOptional>
                      {({ labelId }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef(new AbortController());

                        const loadOptions = createLoadOptions(
                          aborter,
                          searchTerm,
                          waterproofKitApi.getAdminWaterproofKits,
                        );

                        // Fetch initial data on mount
                        useEffect(() => {
                          loadOptions(searchTerm.current).then(setDefaultWaterproofKitOptions);
                        }, []);

                        return (
                          <CustomAsyncSelect
                            {...field}
                            inputId={labelId}
                            defaultOptions={defaultWaterproofKitOptions}
                            isDisabled={isDisabled || isLoading || isSubmitting}
                            loadOptions={loadOptions}
                            isClearable
                            getOptionLabel={(x) => itemLabel(x.item)}
                            getOptionValue={(x) => x.item.id}
                            isInvalid={fieldState.invalid}
                            onChange={(value) => setValue("waterproofKit", value ?? null)}
                          />
                        );
                      }}
                    </FormField>
                  </>
                );
              }}
            />
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isDisabled || isLoading || isSubmitting}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default BaseForm;
